/**
 * The station-volumes section of a run dialog — one component, shared by
 * `RunSurfaceDifferenceDialog` and `RunDeviationAnalysisDialog` (spec §10.4).
 * It owns the controls, the live estimate and the 3D swath preview; the parent
 * owns the form and computes `derived` once with `deriveStationVolumes`, so the
 * Run button and this section never disagree about what is blocking.
 *
 * The public contract Tasks 5–7 consume:
 *  - this component, `StationVolumesFields` / `StationVolumesFieldsProps`;
 *  - from `@/lib/stationVolumes/dialogModel`: `stationVolumesZodShape` +
 *    `refineStationVolumes` (the schema fragment), `stationVolumesDefaults` +
 *    `StationVolumesInitialParams` (the `defaultValues`), `deriveStationVolumes`
 *    → `StationVolumesDerived` (render + blocking), `stationVolumesPayload`
 *    (the mutation block), `stationGridResolution` (the deviation path's
 *    station grid) and `DEFAULT_MIN_COVERAGE_PCT`.
 *
 * This component is form-library agnostic on purpose: values in, patches out.
 * The parent decides what a patch means — in particular the linked-swath
 * mirroring lives in the dialogs' `applyStation`, not here, so both dialogs get
 * it from the same one-line handler.
 */
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { alignmentDrapeOverridesAtom, alignmentDrapeSourcesAtom, alignmentsAtom } from '@/atoms/alignment.atom';
import { crestSourcesAtom } from '@/atoms/crestLine.atom';
import { layersAtom } from '@/atoms/layers.atom';
import { stationVolumePreviewAtom } from '@/atoms/stationVolumes.atom';
import { degradationList, formatStation, internalToDisplay, type Alignment } from '@/core/alignment';
import type { FeatureResult } from '@/hooks/useFeature';
import {
  INTERVAL_PRESETS,
  MAX_BINS,
  MAX_CROSSFALL_TOLERANCE_PCT,
  MAX_SECTION_BYTES,
  MIN_CROSSFALL_BAND_WIDTH_M,
  MIN_CROSSFALL_TOLERANCE_PCT,
  WARN_BINS,
  formatMiB,
  formatSectionBytes,
  type StationVolumesDerived,
  type StationVolumesFormValues,
} from '@/lib/stationVolumes/dialogModel';
import { FIT_SWATH_SCAN_M, fitSwathToSurface } from '@/lib/stationVolumes/fitSwath';
import { formatFixed } from '@/lib/stationVolumes/format';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumericField } from '@/components/ui/numeric-field';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UpgradeBadge } from '@/components/subscription/FeatureGate';

export interface StationVolumesFieldsProps {
  values: StationVolumesFormValues;
  onChange: (patch: Partial<StationVolumesFormValues>) => void;
  /** The grid the worker will bin on: the surface-difference `params.resolution`,
   *  or `stationGridResolution(volumeResolution)` on the deviation path. */
  resolution: number;
  derived: StationVolumesDerived;
  /** The `analysis.stationVolumes` gate, as returned by `useFeature`. */
  gate: Pick<FeatureResult, 'allowed' | 'gate' | 'promptUpgrade'>;
  /** Rendered under the enable checkbox while the section is on — the
   *  deviation dialog's "volume statistics turned on" note. */
  enabledNote?: ReactNode;
  /**
   * The layer the run measures AGAINST — the top surface of a surface
   * difference, the reference model of a deviation run. When it is a LandXML
   * surface loaded in the viewer (it has a sampler in `crestSourcesAtom`),
   * "Fit to surface" sets the swath to that surface's reach along the
   * alignment, so the corridor is the road and not a guess around it.
   */
  referenceLayerId?: string;
}

export function StationVolumesFields({
  values,
  onChange,
  resolution,
  derived,
  gate,
  enabledNote,
  referenceLayerId,
}: StationVolumesFieldsProps) {
  const { t, i18n } = useTranslation('project');
  const allLayers = useAtomValue(layersAtom);
  const alignments = useAtomValue(alignmentsAtom);
  const drapeSources = useAtomValue(alignmentDrapeSourcesAtom);
  const [drapeOverrides, setDrapeOverrides] = useAtom(alignmentDrapeOverridesAtom);
  const crestSources = useAtomValue(crestSourcesAtom);
  const setPreview = useSetAtom(stationVolumePreviewAtom);
  const referenceSampler = referenceLayerId ? (crestSources.get(referenceLayerId)?.sampler ?? null) : null;
  /** The last fit's outcome, shown under the swath row: what it set, or why
   *  it set nothing. Stamped with the inputs it was computed from, so it
   *  disappears (rather than lying) once any of them changes. */
  const [fitNote, setFitNote] = useState<
    { key: string; kind: 'done'; left: number; right: number } | { key: string; kind: 'none' } | null
  >(null);

  const {
    stationVolumesEnabled: svEnabled,
    alignmentKey,
    stationInterval: interval,
    swathLeft,
    swathRight,
    swathLinked,
    stationFromText: fromText,
    stationToText: toText,
    offsetBin,
    crossfallEnabled,
    crossfallLeftEnabled,
    crossfallRightEnabled,
    crossfallLeftFrom,
    crossfallLeftTo,
    crossfallRightFrom,
    crossfallRightTo,
    crossfallLinked,
    crossfallTolerancePct,
  } = values;
  const {
    alignment,
    estimate,
    fromInternal,
    toInternal,
    rangeInvalidKind,
    rangeInvalidText,
    rangeInvalid,
    rangeTooShort,
    tooManyBins,
    sectionTooBig,
    offsetBinClamped,
    intervalOutOfBounds,
    swathLeftOutOfBounds,
    swathRightOutOfBounds,
    offsetBinOutOfBounds,
    crossfallLeftIssue,
    crossfallRightIssue,
    crossfallToleranceInvalid,
    crossfallNoBand,
  } = derived;

  const alignmentList = useMemo(
    () => [...alignments.values()].sort((a, b) => a.name.localeCompare(b.name)),
    [alignments],
  );

  // Alignment picker grouped by owning layer (fix round 1, item 8) — same
  // grouping as AlignmentWalkToolbar.tsx's `alignmentGroups`.
  const alignmentGroups = useMemo(() => {
    const layerNames = new Map(allLayers.map((l) => [l.id, l.name]));
    const byLayer = new Map<string, { layerId: string; label: string; items: Alignment[] }>();
    for (const a of alignments.values()) {
      const g =
        byLayer.get(a.layerId) ??
        byLayer
          .set(a.layerId, { layerId: a.layerId, label: layerNames.get(a.layerId) ?? a.layerId, items: [] })
          .get(a.layerId)!;
      g.items.push(a);
    }
    for (const g of byLayer.values()) g.items.sort((x, y) => x.name.localeCompare(y.name));
    return [...byLayer.values()].sort((x, y) => x.label.localeCompare(y.label));
  }, [alignments, allLayers]);

  // ── Flat alignments ────────────────────────────────────────────────────
  // A LandXML `<CoordGeom>` with Z = 0 and no `<Profile>` parses to
  // `zSource: 'NONE'`: a centreline lying on the datum plane. Nothing the
  // worker computes cares (stationing and offsets are horizontal, and the
  // section elevations come from the compared surfaces), but the swath
  // preview, the corridor bands and the walk clip box are all placed ON the
  // alignment, so at Z = 0 they sit hundreds of metres below the ground.
  //
  // Draping is offered here rather than in the layer drawer because this is
  // where the user is looking at the corridor, and it is applied to the
  // registry (`alignmentDrapeOverridesAtom`) rather than to this form, so the
  // one choice also fixes the walk toolbar and the 3D overlay.
  const drapeLayers = useMemo(() => {
    const layerNames = new Map(allLayers.map((l) => [l.id, l.name]));
    return [...drapeSources.keys()]
      .map((id) => ({ id, name: layerNames.get(id) ?? id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [drapeSources, allLayers]);
  // `''` is "the user said no", which is NOT the same as "not asked yet":
  // only the latter may be filled in by the default below.
  const drapeChoice = alignmentKey ? drapeOverrides.get(alignmentKey) : undefined;
  const drapedLayerName =
    alignment?.zSource === 'DRAPED' && drapeChoice
      ? (drapeLayers.find((l) => l.id === drapeChoice)?.name ?? drapeChoice)
      : null;
  // Show the block while it is relevant OR while it is the reason it is no
  // longer relevant — keyed on `zSource === 'NONE'` alone, a successful drape
  // would remove the only control that can undo it.
  const showDrapeRow = !!alignment && (alignment.zSource === 'NONE' || drapeChoice !== undefined);
  // Radix reads `''` as "no value" and would show the placeholder, so a
  // DECLINED drape (stored as `''`) has to come back as the sentinel or the
  // control forgets the answer the user gave it.
  const drapeValue = drapeChoice === '' ? '__none' : (drapeChoice ?? '');
  const setDrape = (layerId: string) => {
    if (!alignmentKey) return;
    setDrapeOverrides((prev) => {
      const next = new Map(prev);
      next.set(alignmentKey, layerId === '__none' ? '' : layerId);
      return next;
    });
  };

  // Default: the surface the run measures against, when it is one we can
  // sample. The corridor preview then lands on the ground with no extra click.
  // Guarded on `drapeChoice === undefined` so it never overrides a choice the
  // user already made — including "don't drape".
  const canDrapeReference = !!referenceLayerId && drapeSources.has(referenceLayerId);
  useEffect(() => {
    if (!svEnabled || !alignmentKey || drapeChoice !== undefined) return;
    if (!canDrapeReference || alignment?.zSource !== 'NONE') return;
    setDrape(referenceLayerId!);
    // `setDrape` is stable enough for this guard-heavy effect; re-running on a
    // fresh closure would only recompute the same no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svEnabled, alignmentKey, drapeChoice, canDrapeReference, alignment?.zSource, referenceLayerId]);

  // Live swath ribbon in the 3D scene. Effect (not render) because it writes
  // to a global atom; cleared on unmount so closing the dialog removes it.
  useEffect(() => {
    if (!svEnabled || !alignment || rangeInvalid) {
      setPreview(null);
      return;
    }
    setPreview({ key: alignment.key, swathLeft, swathRight, from: fromInternal, to: toInternal });
  }, [svEnabled, alignment, swathLeft, swathRight, fromInternal, toInternal, rangeInvalid, setPreview]);
  useEffect(() => () => setPreview(null), [setPreview]);

  const fitFrom = rangeInvalid || !alignment ? 0 : fromInternal;
  const fitTo = rangeInvalid || !alignment ? (alignment?.length ?? 0) : toInternal;
  const fitKey = `${alignmentKey}|${referenceLayerId ?? ''}|${fitFrom}|${fitTo}`;
  const fitSwath = () => {
    if (!alignment || !referenceSampler) return;
    const fit = fitSwathToSurface(alignment, referenceSampler, fitFrom, fitTo);
    if (!fit) {
      setFitNote({ key: fitKey, kind: 'none' });
      return;
    }
    // Unlink first: a fitted swath is asymmetric whenever the road is, and the
    // dialogs' `applyStation` mirrors a linked pair AFTER applying `swathLinked`.
    onChange({ swathLinked: false, swathLeft: fit.left, swathRight: fit.right });
    setFitNote({ key: fitKey, kind: 'done', left: fit.left, right: fit.right });
  };
  // The "done" note also goes once the user edits the swath it reported.
  const showFitNote =
    fitNote != null &&
    fitNote.key === fitKey &&
    (fitNote.kind === 'none' || (fitNote.left === swathLeft && fitNote.right === swathRight));

  // Stable ids so plain <Label htmlFor> pairs correctly with each control —
  // these fields aren't wrapped in <FormField>/<FormItem>, so <FormLabel>'s
  // `formItemId` context isn't available (fix round 1, item 9: it rendered
  // `htmlFor="undefined-form-item"` and labelled nothing).
  const alignmentSelectId = useId();
  const drapeSelectId = useId();
  const intervalCustomId = useId();
  const swathLeftId = useId();
  const enableId = useId();
  const swathRightId = useId();
  const stationFromId = useId();
  const stationToId = useId();
  const offsetBinId = useId();
  const crossfallEnableId = useId();
  const crossfallLeftOnId = useId();
  const crossfallRightOnId = useId();
  const crossfallLeftFromId = useId();
  const crossfallLeftToId = useId();
  const crossfallRightFromId = useId();
  const crossfallRightToId = useId();
  const crossfallToleranceId = useId();

  return (
    <div
      data-testid='sv-section'
      // `overflow-x-clip`, NOT `overflow-x-hidden`. Both clip horizontally,
      // which is what the structural guard here wants at narrow dialog widths
      // — but they differ on the OTHER axis (CSS Overflow §3):
      //   `overflow-x: hidden` + `overflow-y: visible` → y computes to AUTO,
      //     so the box silently becomes its own vertical scroll container.
      //   `overflow-x: clip`   + `overflow-y: visible` → y computes to HIDDEN,
      //     which is not a scroll container and can never show a scrollbar.
      // With `hidden` this box grew a second scrollbar nested inside the
      // dialog body's own `overflow-y-auto` (`RunSurfaceDifferenceDialog.tsx:392`),
      // visible around the "Register an alignment first…" empty state.
      // Nothing is clipped that was not clipped before: the box's height is
      // content-driven, so there is no vertical overflow to hide.
      className='min-w-0 overflow-x-clip rounded-md border border-slate-200 p-3 dark:border-stone-700'>
      {/* Same shape as the dialogs' other checkbox rows (`FormItem` +
          `FormLabel`): a flex row, the shared `Checkbox`, and the shared
          `Label` at its text-sm/medium size. */}
      <div className='flex items-center gap-2'>
        <Checkbox
          id={enableId}
          data-testid='sv-enable'
          checked={svEnabled}
          // `disabled` only for the data condition (no alignment to bin
          // against). When the gate is what stops the user the control
          // must stay clickable, or the upgrade dialog is unreachable.
          disabled={gate.allowed && alignmentList.length === 0}
          aria-disabled={gate.allowed ? undefined : true}
          data-locked={gate.allowed ? undefined : 'true'}
          onCheckedChange={(c) => {
            if (!gate.allowed) return gate.promptUpgrade();
            onChange({
              stationVolumesEnabled: !!c,
              // Auto-select on tick: the parent no longer sees the checkbox,
              // so the "nothing selected yet" default is decided here.
              ...(c && !alignmentKey && alignmentList[0] ? { alignmentKey: alignmentList[0].key } : {}),
            });
          }}
        />
        <Label htmlFor={enableId} className={gate.allowed ? '' : 'opacity-60'}>
          {t('stationVolumes.dialog.enable')}
        </Label>
        {!gate.allowed && <UpgradeBadge gate={gate.gate} />}
      </div>
      {alignmentList.length === 0 && (
        <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>{t('stationVolumes.dialog.noAlignments')}</p>
      )}
      {/* Locked, the section collapses to the muted header row above: the
          reader still learns the feature exists (and what unlocks it)
          without getting controls that would submit a paid analysis. */}
      {svEnabled && gate.allowed && alignmentList.length > 0 && (
        <div className='mt-3 flex flex-col gap-3'>
          {enabledNote}
          {/* Plain values-in / patches-out controls below, not
              `<FormField control=.../>`: this section is shared by two
              dialogs whose forms have different shapes (and whose zod
              `.superRefine()` already breaks react-hook-form's Control
              generic for every bound field — pre-existing, tracked in the
              tsc baseline). Each control gets its own `<Label htmlFor>` +
              id instead of `<FormLabel>`, which needs a `<FormField>`/
              `<FormItem>` ancestor it doesn't have here (fix round 1,
              item 9 — without one it silently emitted
              `htmlFor="undefined-form-item"` and labelled nothing). */}
          <div className='min-w-0'>
            <Label htmlFor={alignmentSelectId}>{t('stationVolumes.dialog.alignment')}</Label>
            <Select onValueChange={(v) => onChange({ alignmentKey: v })} value={alignmentKey}>
              <SelectTrigger
                id={alignmentSelectId}
                data-testid='sv-alignment-select'
                data-value={alignmentKey}
                className='w-full'>
                <SelectValue placeholder={t('stationVolumes.dialog.selectAlignment')} />
              </SelectTrigger>
              <SelectContent>
                {alignmentGroups.map((g) => (
                  <SelectGroup key={g.layerId}>
                    <SelectLabel className='truncate'>{g.label}</SelectLabel>
                    {g.items.map((a) => (
                      <SelectItem key={a.key} value={a.key}>
                        <span className='min-w-0 flex-1 truncate'>
                          {a.name} · {formatFixed(a.length, 0)} m
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            {svEnabled && !alignment && (
              <p className='mt-1 text-sm text-destructive break-words'>
                {t('stationVolumes.dialog.alignmentRequired')}
              </p>
            )}
            {/* Chord fallbacks (corridor-trust T6c): the parser could not
                reproduce a <Spiral>/<Curve> and drew a straight chord, so the
                corridor this run bins against cuts that corner. Amber, not
                destructive — the run is still worth making, it just is not
                measuring the curve the design draws. */}
            {alignment && alignment.degradations.length > 0 && (
              <p
                data-testid='sv-alignment-degraded'
                className='mt-1 text-xs break-words text-amber-700 dark:text-amber-400'>
                {t('alignments.degraded.volumesNote', { list: degradationList(alignment, t) })}
              </p>
            )}
            {/* No vertical profile (Z = 0): the volumes are fine, the 3D
                placement is not. Amber like the chord-fallback note above,
                and for the same reason — worth saying, not worth blocking. */}
            {showDrapeRow && alignment.zSource === 'NONE' && (
              <p
                data-testid='sv-alignment-no-profile'
                className='mt-1 text-xs break-words text-amber-700 dark:text-amber-400'>
                {t('stationVolumes.dialog.alignmentNoProfile')}
              </p>
            )}
            {showDrapeRow && drapedLayerName && (
              <p
                data-testid='sv-alignment-draped'
                className='mt-1 text-xs break-words text-slate-600 dark:text-stone-400'>
                {t('stationVolumes.dialog.alignmentDraped', { layer: drapedLayerName })}
              </p>
            )}
            {showDrapeRow && drapeLayers.length > 0 && (
              <div className='mt-1'>
                <Label htmlFor={drapeSelectId}>{t('stationVolumes.dialog.drapeOnto')}</Label>
                <Select value={drapeValue} onValueChange={setDrape}>
                  <SelectTrigger
                    id={drapeSelectId}
                    data-testid='sv-drape-select'
                    data-value={drapeValue}
                    className='w-full'>
                    <SelectValue placeholder={t('stationVolumes.dialog.drapePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Radix treats '' as "no value", so the decline option
                        carries a sentinel and is mapped back on the way in. */}
                    <SelectItem value='__none' data-testid='sv-drape-option-none'>
                      {t('stationVolumes.dialog.drapeNone')}
                    </SelectItem>
                    {drapeLayers.map((l) => (
                      <SelectItem key={l.id} value={l.id} data-testid={`sv-drape-option-${l.id}`}>
                        <span className='min-w-0 flex-1 truncate'>{l.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div>
            <Label>{t('stationVolumes.dialog.interval')}</Label>
            <div className='mt-1 flex flex-wrap items-center gap-1'>
              {INTERVAL_PRESETS.map((p) => (
                <Button
                  key={p}
                  type='button'
                  size='sm'
                  variant={interval === p ? 'default' : 'outline'}
                  onClick={() => onChange({ stationInterval: p })}>
                  {p} m
                </Button>
              ))}
              <div className='flex items-center gap-1'>
                <div className='w-24'>
                  <NumericField
                    id={intervalCustomId}
                    aria-label={t('stationVolumes.dialog.interval')}
                    value={interval}
                    onChange={(v) => onChange({ stationInterval: v })}
                    step='1'
                    min='1'
                    max='500'
                  />
                </div>
                <span className='text-xs text-muted-foreground'>{t('stationVolumes.dialog.metresSuffix')}</span>
              </div>
            </div>
            {intervalOutOfBounds && (
              <p className='mt-1 text-sm text-destructive'>{t('stationVolumes.errors.intervalRange')}</p>
            )}
          </div>
          <div data-testid='sv-swath-row' className='flex items-end gap-2'>
            <div className='min-w-0 flex-1'>
              <Label htmlFor={swathLeftId}>{t('stationVolumes.dialog.swathLeft')}</Label>
              <div className='mt-1 flex min-w-0 items-center gap-1'>
                <NumericField
                  id={swathLeftId}
                  value={swathLeft}
                  onChange={(v) => onChange({ swathLeft: v })}
                  step='0.5'
                  min='0'
                  max='500'
                />
                <span className='text-xs text-muted-foreground'>{t('stationVolumes.dialog.metresSuffix')}</span>
              </div>
              {swathLeftOutOfBounds && (
                <p className='mt-1 text-sm text-destructive break-words'>{t('stationVolumes.errors.swathRange')}</p>
              )}
            </div>
            <Button
              type='button'
              size='sm'
              variant={swathLinked ? 'default' : 'outline'}
              aria-pressed={swathLinked}
              className='min-w-0'
              onClick={() => onChange({ swathLinked: !swathLinked })}>
              {t('stationVolumes.dialog.linked')}
            </Button>
            {referenceLayerId !== undefined && (
              // A data condition, not a gate: the plain `disabled` is right
              // here (the upgrade rule is about paywalls). Rendered whenever
              // the dialog names a reference so the affordance is discoverable
              // even for a surface type that has no sampler in the browser.
              <Button
                type='button'
                size='sm'
                variant='outline'
                className='min-w-0'
                data-testid='sv-fit-swath'
                disabled={!alignment || !referenceSampler}
                title={
                  !referenceSampler
                    ? t('stationVolumes.dialog.fitSwathUnavailable')
                    : t('stationVolumes.dialog.fitSwathHelp')
                }
                onClick={fitSwath}>
                {t('stationVolumes.dialog.fitSwath')}
              </Button>
            )}
            <div className='min-w-0 flex-1'>
              <Label htmlFor={swathRightId}>{t('stationVolumes.dialog.swathRight')}</Label>
              <div className='mt-1 flex min-w-0 items-center gap-1'>
                <NumericField
                  id={swathRightId}
                  value={swathRight}
                  onChange={(v) => onChange({ swathRight: v })}
                  step='0.5'
                  min='0'
                  max='500'
                />
                <span className='text-xs text-muted-foreground'>{t('stationVolumes.dialog.metresSuffix')}</span>
              </div>
              {swathRightOutOfBounds && (
                <p className='mt-1 text-sm text-destructive break-words'>{t('stationVolumes.errors.swathRange')}</p>
              )}
            </div>
          </div>
          {swathLeft + swathRight <= 0 && (
            <p className='text-sm text-destructive'>{t('stationVolumes.dialog.swathZero')}</p>
          )}
          {showFitNote && fitNote.kind === 'done' && (
            <p className='text-xs text-slate-600 dark:text-stone-400' data-testid='sv-fit-swath-note'>
              {t('stationVolumes.dialog.fitSwathDone', { left: formatFixed(fitNote.left, 1), right: formatFixed(fitNote.right, 1) })}
            </p>
          )}
          {showFitNote && fitNote.kind === 'none' && (
            <p className='text-xs text-amber-700 dark:text-amber-400' data-testid='sv-fit-swath-note'>
              {t('stationVolumes.dialog.fitSwathNoData', { scan: FIT_SWATH_SCAN_M })}
            </p>
          )}
          <div className='flex items-end gap-2'>
            <div className='min-w-0 flex-1'>
              <Label htmlFor={stationFromId}>{t('stationVolumes.dialog.stationFrom')}</Label>
              <Input
                id={stationFromId}
                className='w-full min-w-0'
                value={fromText}
                placeholder={alignment ? formatStation(internalToDisplay(alignment, 0)) : ''}
                onChange={(e) => onChange({ stationFromText: e.target.value })}
              />
            </div>
            <div className='min-w-0 flex-1'>
              <Label htmlFor={stationToId}>{t('stationVolumes.dialog.stationTo')}</Label>
              <Input
                id={stationToId}
                className='w-full min-w-0'
                value={toText}
                placeholder={alignment ? formatStation(internalToDisplay(alignment, alignment.length)) : ''}
                onChange={(e) => onChange({ stationToText: e.target.value })}
              />
            </div>
            <Button
              type='button'
              size='sm'
              variant='outline'
              disabled={!alignment}
              onClick={() => {
                if (!alignment) return;
                // Fill with the resolved start/end stations rather than
                // clearing the inputs — with empty inputs "empty = full
                // length" is correct but invisible, so the button reads
                // as doing nothing. Parsing still treats empty text as
                // full length (`parseStationRange`'s defaults), so this
                // button is a convenience, not a change in semantics.
                onChange({
                  stationFromText: formatStation(internalToDisplay(alignment, 0)),
                  stationToText: formatStation(internalToDisplay(alignment, alignment.length)),
                });
              }}>
              {t('stationVolumes.dialog.fullLength')}
            </Button>
          </div>
          {rangeInvalidKind === 'unparseable' && (
            <p className='text-sm text-destructive'>{t('stationVolumes.errors.stationUnparseable')}</p>
          )}
          {rangeInvalidKind === 'outOfRange' && alignment && (
            <p className='text-sm text-destructive break-words'>
              {t('stationVolumes.errors.stationOutOfRange', {
                from: formatStation(internalToDisplay(alignment, 0)),
                length: formatStation(internalToDisplay(alignment, alignment.length)),
              })}
            </p>
          )}
          {rangeInvalidKind === 'ambiguous' && (
            <p className='text-sm text-destructive'>
              {t('stationVolumes.dialog.ambiguousStation', { station: rangeInvalidText })}
            </p>
          )}
          {rangeTooShort && <p className='text-sm text-destructive'>{t('stationVolumes.dialog.rangeTooShort')}</p>}
          <div>
            <Label htmlFor={offsetBinId}>{t('stationVolumes.dialog.sectionDetail')}</Label>
            <div className='mt-1 flex items-center gap-1'>
              <div className='w-24'>
                <NumericField
                  id={offsetBinId}
                  value={offsetBin}
                  onChange={(v) => onChange({ offsetBin: v })}
                  step='0.1'
                  min='0.1'
                  max='5'
                />
              </div>
              <span className='text-xs text-muted-foreground'>{t('stationVolumes.dialog.offsetBinSuffix')}</span>
            </div>
            {offsetBinOutOfBounds ? (
              <p className='mt-1 text-sm text-destructive'>{t('stationVolumes.errors.offsetBinRange')}</p>
            ) : (
              offsetBinClamped && (
                <p className='mt-1 text-xs text-amber-600'>
                  {t('stationVolumes.dialog.offsetBinClamped', { resolution })}
                </p>
              )
            )}
          </div>
          {/* --- Crossfall check (crossfall spec §3) ---------------------
              A disclosure, not an always-open row: it is an optional second
              question about the same corridor, and a run dialog that asks
              every optional question at once is unreadable. */}
          <div data-testid='sv-crossfall'>
            <div className='flex items-center gap-2'>
              <Checkbox
                id={crossfallEnableId}
                data-testid='sv-crossfall-enable'
                checked={crossfallEnabled}
                onCheckedChange={(c) => onChange({ crossfallEnabled: !!c })}
              />
              <Label htmlFor={crossfallEnableId}>{t('stationVolumes.dialog.crossfallTitle')}</Label>
            </div>
            {crossfallEnabled && (
              <div data-testid='sv-crossfall-body' className='mt-2 space-y-2'>
                <p className='text-xs text-slate-600 dark:text-stone-400'>{t('stationVolumes.dialog.crossfallHint')}</p>
                {/* One group per side, each behind its own switch: either
                    band may be omitted (spec §3), and on a one-sided corridor
                    — `swathLeft: 0` is a legal run — switching the side off is
                    the ONLY way to ask for a check the swath can answer. */}
                <div className='flex items-end gap-2'>
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        id={crossfallLeftOnId}
                        data-testid='sv-crossfall-left-enable'
                        checked={crossfallLeftEnabled}
                        onCheckedChange={(c) => onChange({ crossfallLeftEnabled: !!c })}
                      />
                      <Label htmlFor={crossfallLeftOnId}>{t('stationVolumes.dialog.crossfallLeftSide')}</Label>
                    </div>
                    {crossfallLeftEnabled && (
                      <div className='mt-1 flex items-end gap-2'>
                        <div className='min-w-0 flex-1'>
                          <Label htmlFor={crossfallLeftFromId}>{t('stationVolumes.dialog.crossfallLeftFrom')}</Label>
                          <div className='mt-1 flex min-w-0 items-center gap-1'>
                            <NumericField
                              id={crossfallLeftFromId}
                              data-testid='sv-crossfall-left-from'
                              value={crossfallLeftFrom}
                              onChange={(v) => onChange({ crossfallLeftFrom: v })}
                              step='0.25'
                              min='0'
                              max='500'
                            />
                            <span className='text-xs text-muted-foreground'>
                              {t('stationVolumes.dialog.metresSuffix')}
                            </span>
                          </div>
                        </div>
                        <div className='min-w-0 flex-1'>
                          <Label htmlFor={crossfallLeftToId}>{t('stationVolumes.dialog.crossfallLeftTo')}</Label>
                          <div className='mt-1 flex min-w-0 items-center gap-1'>
                            <NumericField
                              id={crossfallLeftToId}
                              data-testid='sv-crossfall-left-to'
                              value={crossfallLeftTo}
                              onChange={(v) => onChange({ crossfallLeftTo: v })}
                              step='0.25'
                              min='0'
                              max='500'
                            />
                            <span className='text-xs text-muted-foreground'>
                              {t('stationVolumes.dialog.metresSuffix')}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Same affordance as the swath link, and the same division
                      of labour: this reports the toggle, the dialogs'
                      `applyStation` decides what a linked edit means. */}
                  <Button
                    type='button'
                    size='sm'
                    variant={crossfallLinked ? 'default' : 'outline'}
                    aria-pressed={crossfallLinked}
                    data-testid='sv-crossfall-link'
                    className='min-w-0'
                    onClick={() => onChange({ crossfallLinked: !crossfallLinked })}>
                    {t('stationVolumes.dialog.crossfallLinked')}
                  </Button>
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        id={crossfallRightOnId}
                        data-testid='sv-crossfall-right-enable'
                        checked={crossfallRightEnabled}
                        onCheckedChange={(c) => onChange({ crossfallRightEnabled: !!c })}
                      />
                      <Label htmlFor={crossfallRightOnId}>{t('stationVolumes.dialog.crossfallRightSide')}</Label>
                    </div>
                    {crossfallRightEnabled && (
                      <div className='mt-1 flex items-end gap-2'>
                        <div className='min-w-0 flex-1'>
                          <Label htmlFor={crossfallRightFromId}>{t('stationVolumes.dialog.crossfallRightFrom')}</Label>
                          <div className='mt-1 flex min-w-0 items-center gap-1'>
                            <NumericField
                              id={crossfallRightFromId}
                              data-testid='sv-crossfall-right-from'
                              value={crossfallRightFrom}
                              onChange={(v) => onChange({ crossfallRightFrom: v })}
                              step='0.25'
                              min='0'
                              max='500'
                            />
                            <span className='text-xs text-muted-foreground'>
                              {t('stationVolumes.dialog.metresSuffix')}
                            </span>
                          </div>
                        </div>
                        <div className='min-w-0 flex-1'>
                          <Label htmlFor={crossfallRightToId}>{t('stationVolumes.dialog.crossfallRightTo')}</Label>
                          <div className='mt-1 flex min-w-0 items-center gap-1'>
                            <NumericField
                              id={crossfallRightToId}
                              data-testid='sv-crossfall-right-to'
                              value={crossfallRightTo}
                              onChange={(v) => onChange({ crossfallRightTo: v })}
                              step='0.25'
                              min='0'
                              max='500'
                            />
                            <span className='text-xs text-muted-foreground'>
                              {t('stationVolumes.dialog.metresSuffix')}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* The message about THIS user's problem: a band the swath has
                    no columns for is not a band that is too narrow, and the
                    fix (a wider swath, or this side switched off) is a
                    different one. */}
                {crossfallLeftIssue && (
                  <p data-testid='sv-crossfall-left-error' className='text-sm break-words text-destructive'>
                    {crossfallLeftIssue === 'swath'
                      ? t('stationVolumes.errors.crossfallSwath')
                      : t('stationVolumes.errors.crossfallBandRange', { min: MIN_CROSSFALL_BAND_WIDTH_M })}
                  </p>
                )}
                {crossfallRightIssue && (
                  <p data-testid='sv-crossfall-right-error' className='text-sm break-words text-destructive'>
                    {crossfallRightIssue === 'swath'
                      ? t('stationVolumes.errors.crossfallSwath')
                      : t('stationVolumes.errors.crossfallBandRange', { min: MIN_CROSSFALL_BAND_WIDTH_M })}
                  </p>
                )}
                {crossfallNoBand && (
                  <p data-testid='sv-crossfall-none-error' className='text-sm break-words text-destructive'>
                    {t('stationVolumes.errors.crossfallNoBand')}
                  </p>
                )}
                <div>
                  <Label htmlFor={crossfallToleranceId}>{t('stationVolumes.dialog.crossfallTolerance')}</Label>
                  <div className='mt-1 flex items-center gap-1'>
                    <div className='w-24'>
                      <NumericField
                        id={crossfallToleranceId}
                        data-testid='sv-crossfall-tolerance'
                        value={crossfallTolerancePct}
                        onChange={(v) => onChange({ crossfallTolerancePct: v })}
                        step='0.05'
                        min='0.05'
                        max='5'
                      />
                    </div>
                    <span className='text-xs text-muted-foreground'>
                      {t('stationVolumes.dialog.crossfallToleranceSuffix')}
                    </span>
                  </div>
                  {crossfallToleranceInvalid && (
                    <p data-testid='sv-crossfall-tolerance-error' className='mt-1 text-sm text-destructive'>
                      {t('stationVolumes.errors.crossfallTolerance', {
                        min: MIN_CROSSFALL_TOLERANCE_PCT,
                        max: MAX_CROSSFALL_TOLERANCE_PCT,
                      })}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          {estimate && !rangeInvalid && (
            <p
              data-testid='sv-estimate'
              data-warn={String(estimate.binCount > WARN_BINS && !tooManyBins && !sectionTooBig)}
              className={
                tooManyBins || sectionTooBig
                  ? 'text-sm break-words text-destructive'
                  : estimate.binCount > WARN_BINS
                    ? 'text-sm break-words text-amber-600'
                    : 'text-sm break-words text-slate-600 dark:text-stone-400'
              }>
              {t('stationVolumes.dialog.estimate', {
                bins: estimate.binCount.toLocaleString(i18n.language),
                area: Math.round(estimate.corridorAreaM2).toLocaleString(i18n.language),
                size: formatSectionBytes(estimate.sectionBytes),
                resolution,
              })}
              {estimate.binCount > WARN_BINS && !tooManyBins && !sectionTooBig
                ? ` ${t('stationVolumes.dialog.manyBinsHint')}`
                : ''}
              {/* A plain space, not " — ": the over-limit fragment is now
                  a predicate on the estimate in front of it ("… at 1 m is
                  over the 20 000-bin limit — narrow the station range …"),
                  and a dash here would open a second one it never closes.
                  At most ONE of the two blocking clauses is appended, for
                  the same reason: two predicates on one estimate is two
                  sentences pretending to be one. Bins first — it is the
                  cause the reader can act on with a single control. */}
              {tooManyBins ? ` ${t('stationVolumes.dialog.tooManyBins', { max: MAX_BINS })}` : ''}
              {sectionTooBig && !tooManyBins
                ? ` ${t('stationVolumes.dialog.sectionTooBig', { max: formatMiB(MAX_SECTION_BYTES) })}`
                : ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
